import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Stethoscope, Activity, FlaskConical, Swords, FileText,
  AlertTriangle, CheckCircle, XCircle, Loader2, Shield,
  HeartPulse, Zap, Eye, ChevronRight, Copy, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/lib/api';

const severityColor = (sev) => {
  const map = { critical: 'text-red-400', urgent: 'text-orange-400', moderate: 'text-yellow-400', stable: 'text-green-400',
    sev1: 'text-red-400', sev2: 'text-orange-400', sev3: 'text-yellow-400', sev4: 'text-green-400',
    high: 'text-red-400', medium: 'text-yellow-400', low: 'text-green-400' };
  return map[sev] || 'text-slate-400';
};

const gradeBg = (grade) => {
  const map = { A: 'bg-green-500/20 text-green-400', B: 'bg-blue-500/20 text-blue-400',
    C: 'bg-yellow-500/20 text-yellow-400', D: 'bg-orange-500/20 text-orange-400', F: 'bg-red-500/20 text-red-400' };
  return map[grade] || 'bg-slate-500/20 text-slate-400';
};

// ── Room 1: The ER ──

function ERTab() {
  const [agentId, setAgentId] = useState('');
  const [triage, setTriage] = useState(null);
  const [stabilization, setStabilization] = useState(null);
  const [loading, setLoading] = useState(false);

  const runTriage = async () => {
    if (!agentId) { toast.error('Enter an agent ID'); return; }
    setLoading(true);
    try {
      const data = await api.triageAgent(agentId);
      setTriage(data);
      setStabilization(null);
      toast.success(`Triage complete: ${data.severity}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Triage failed');
    }
    setLoading(false);
  };

  const stabilize = async () => {
    try {
      const data = await api.stabilizeAgent(agentId);
      setStabilization(data);
      toast.success(`${data.order_count} stabilization orders issued`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Stabilization failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <HeartPulse className="w-5 h-5 text-red-400" /> Agent Triage
        </h3>
        <div className="flex gap-4">
          <Input placeholder="Agent ID (agt_...)" value={agentId} onChange={e => setAgentId(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200 flex-1" />
          <Button onClick={runTriage} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Stethoscope className="w-4 h-4 mr-2" />}
            Triage
          </Button>
        </div>
      </div>

      {triage && (
        <div className={`rounded-xl p-6 border ${
          triage.severity === 'critical' ? 'bg-red-500/5 border-red-500/30' :
          triage.severity === 'urgent' ? 'bg-orange-500/5 border-orange-500/30' :
          triage.severity === 'moderate' ? 'bg-yellow-500/5 border-yellow-500/30' :
          'bg-green-500/5 border-green-500/30'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className={`text-2xl font-bold ${severityColor(triage.severity)}`}>
                {triage.severity.toUpperCase()}
              </span>
              {triage.auto_kill && (
                <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">AUTO-KILL</span>
              )}
            </div>
            {triage.severity !== 'stable' && (
              <Button onClick={stabilize} size="sm" className="bg-orange-600 hover:bg-orange-700 text-white">
                <Zap className="w-4 h-4 mr-1" /> Stabilize
              </Button>
            )}
          </div>

          {triage.symptoms.length > 0 && (
            <div className="space-y-2 mb-4">
              <h4 className="text-sm font-medium text-slate-300">Symptoms</h4>
              {triage.symptoms.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <AlertTriangle className={`w-4 h-4 ${severityColor(s.severity)}`} />
                  <span className="text-slate-300">{s.symptom.replace(/_/g, ' ')}</span>
                  <span className={`text-xs ${severityColor(s.severity)}`}>{s.severity}</span>
                </div>
              ))}
            </div>
          )}

          <div>
            <h4 className="text-sm font-medium text-slate-300 mb-2">Recommended Actions</h4>
            <div className="flex flex-wrap gap-2">
              {triage.recommended_actions.map((a, i) => (
                <span key={i} className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded">
                  {a.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {stabilization && (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
          <h4 className="text-white font-medium mb-3">Stabilization Orders</h4>
          <div className="space-y-2">
            {stabilization.orders.map((o, i) => (
              <div key={i} className="flex items-center gap-3 text-sm bg-slate-800/50 rounded-lg p-3">
                <span className="text-xs text-slate-500 font-mono">P{o.priority}</span>
                <span className="text-orange-400 font-medium">{o.order.replace(/_/g, ' ')}</span>
                <span className="text-slate-500 text-xs">{o.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Room 2: The Radiology Lab ──

function RadiologyTab() {
  const [agentId, setAgentId] = useState('');
  const [traceId, setTraceId] = useState('');
  const [xray, setXray] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);

  const getXray = async () => {
    if (!traceId) { toast.error('Enter a trace ID'); return; }
    setLoading(true);
    try {
      const data = await api.getTraceXray(traceId);
      setXray(data);
      toast.success(`X-Ray: ${data.diagnosis}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'X-Ray failed');
    }
    setLoading(false);
  };

  const getSnapshot = async () => {
    if (!agentId) { toast.error('Enter an agent ID'); return; }
    setLoading(true);
    try {
      const data = await api.getHealthSnapshot(agentId);
      setSnapshot(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Snapshot failed');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5 text-blue-400" /> Trace X-Ray
        </h3>
        <div className="flex gap-4 mb-4">
          <Input placeholder="Trace ID (trace_...)" value={traceId} onChange={e => setTraceId(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200 flex-1" />
          <Button onClick={getXray} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            X-Ray
          </Button>
        </div>

        <div className="flex gap-4">
          <Input placeholder="Agent ID for health snapshot" value={agentId} onChange={e => setAgentId(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200 flex-1" />
          <Button onClick={getSnapshot} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white">
            <Activity className="w-4 h-4 mr-2" /> Snapshot
          </Button>
        </div>
      </div>

      {xray && (
        <div className={`rounded-xl p-6 border ${
          xray.diagnosis === 'unhealthy' ? 'bg-red-500/5 border-red-500/30' :
          xray.diagnosis === 'degraded' ? 'bg-yellow-500/5 border-yellow-500/30' :
          'bg-green-500/5 border-green-500/30'
        }`}>
          <div className="flex items-center gap-3 mb-4">
            <span className={`text-xl font-bold ${
              xray.diagnosis === 'unhealthy' ? 'text-red-400' :
              xray.diagnosis === 'degraded' ? 'text-yellow-400' : 'text-green-400'
            }`}>{xray.diagnosis.toUpperCase()}</span>
            <span className="text-sm text-slate-400">{xray.finding_count} finding(s)</span>
          </div>

          {xray.findings.length > 0 && (
            <div className="space-y-2 mb-4">
              {xray.findings.map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-sm bg-slate-800/50 rounded-lg p-3">
                  <AlertTriangle className={`w-4 h-4 mt-0.5 ${severityColor(f.severity)}`} />
                  <div>
                    <span className="text-slate-200 font-medium">{f.type.replace(/_/g, ' ')}</span>
                    <p className="text-slate-400 text-xs mt-1">{f.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {xray.metrics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['Spans', xray.metrics.total_spans],
                ['Tokens', xray.metrics.total_tokens?.toLocaleString()],
                ['Errors', xray.metrics.error_count],
                ['Latency', `${xray.metrics.total_latency_ms}ms`],
              ].map(([label, val]) => (
                <div key={label} className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <div className="text-xs text-slate-500">{label}</div>
                  <div className="text-lg text-slate-200 font-mono">{val}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {snapshot && (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
          <h4 className="text-white font-medium mb-3">Health Snapshot</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['Health', snapshot.health],
              ['Traces', snapshot.total_traces],
              ['Errors', snapshot.error_traces],
              ['Error Rate', `${(snapshot.error_rate * 100).toFixed(1)}%`],
            ].map(([label, val]) => (
              <div key={label} className="bg-slate-800/50 rounded-lg p-3 text-center">
                <div className="text-xs text-slate-500">{label}</div>
                <div className={`text-lg font-mono ${
                  val === 'critical' ? 'text-red-400' : val === 'degraded' ? 'text-yellow-400' : 'text-slate-200'
                }`}>{val}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Room 3: The Toxicology Screen ──

function ToxicologyTab() {
  const [question, setQuestion] = useState('');
  const [response, setResponse] = useState('');
  const [sources, setSources] = useState('');
  const [facts, setFacts] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runDiagnostic = async () => {
    if (!question || !response || !sources) { toast.error('Fill in question, response, and sources'); return; }
    setLoading(true);
    try {
      const sourceList = sources.split('\n').filter(s => s.trim());
      const factList = facts ? facts.split('\n').filter(f => f.trim()) : undefined;
      const data = await api.runRAGDiagnostic(question, response, sourceList, factList);
      setResult(data);
      toast.success(`RAG health: ${data.overall_health}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Diagnostic failed');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-purple-400" /> RAG Diagnostic
        </h3>
        <div className="space-y-3">
          <Input placeholder="Question asked to the agent" value={question} onChange={e => setQuestion(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" />
          <Textarea placeholder="Agent's response" value={response} onChange={e => setResponse(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" rows={3} />
          <Textarea placeholder="Source documents (one per line)" value={sources} onChange={e => setSources(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" rows={3} />
          <Textarea placeholder="Expected facts (one per line, optional)" value={facts} onChange={e => setFacts(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" rows={2} />
          <Button onClick={runDiagnostic} disabled={loading} className="bg-purple-600 hover:bg-purple-700 text-white">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-2" />}
            Run Diagnostic
          </Button>
        </div>
      </div>

      {result && (
        <div className={`rounded-xl p-6 border ${
          result.overall_health === 'unhealthy' ? 'bg-red-500/5 border-red-500/30' :
          result.overall_health === 'degraded' ? 'bg-yellow-500/5 border-yellow-500/30' :
          'bg-green-500/5 border-green-500/30'
        }`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xl font-bold text-slate-200">Composite: {(result.composite_score * 100).toFixed(1)}%</span>
            <span className={`text-sm px-2 py-1 rounded ${
              result.overall_health === 'healthy' ? 'bg-green-500/20 text-green-400' :
              result.overall_health === 'degraded' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-red-500/20 text-red-400'
            }`}>{result.overall_health}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: 'Faithfulness', data: result.faithfulness, icon: '🎯' },
              { label: 'Relevance', data: result.relevance, icon: '📎' },
              { label: 'Context Recall', data: result.context_recall, icon: '🔍' },
            ].map(({ label, data, icon }) => (
              <div key={label} className="bg-slate-800/50 rounded-lg p-4">
                <div className="text-sm text-slate-400 mb-1">{icon} {label}</div>
                <div className="text-2xl font-mono text-slate-200">{(data.score * 100).toFixed(1)}%</div>
                <div className={`text-xs ${
                  data.status === 'healthy' ? 'text-green-400' :
                  data.status === 'degraded' ? 'text-yellow-400' : 'text-red-400'
                }`}>{data.status}</div>
              </div>
            ))}
          </div>

          {result.faithfulness?.ungrounded_claims?.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-slate-300 mb-2">Ungrounded Claims</h4>
              {result.faithfulness.ungrounded_claims.map((claim, i) => (
                <div key={i} className="text-sm text-red-400/80 bg-red-500/5 rounded p-2 mb-1 font-mono">
                  {claim}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Room 4: The Operating Room (Red Team) ──

function RedTeamTab() {
  const [agentId, setAgentId] = useState('');
  const [catalog, setCatalog] = useState(null);
  const [run, setRun] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCatalog = async () => {
    try {
      const data = await api.getAttackCatalog();
      setCatalog(data);
    } catch (err) {
      toast.error('Failed to load catalog');
    }
  };

  const startRun = async () => {
    if (!agentId) { toast.error('Enter an agent ID'); return; }
    setLoading(true);
    try {
      const data = await api.startRedTeamRun(agentId);
      setRun(data);
      setSummary(null);
      toast.success(`Red team run started: ${data.total_vectors} vectors`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start run');
    }
    setLoading(false);
  };

  const getSummary = async () => {
    if (!run?.run_id) return;
    try {
      const data = await api.getRedTeamSummary(run.run_id);
      setSummary(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to get summary');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <Swords className="w-5 h-5 text-red-400" /> Red Team Testing
        </h3>
        <div className="flex gap-4 mb-4">
          <Input placeholder="Agent ID (agt_...)" value={agentId} onChange={e => setAgentId(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200 flex-1" />
          <Button onClick={startRun} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Swords className="w-4 h-4 mr-2" />}
            Start Run
          </Button>
          <Button onClick={loadCatalog} variant="outline" className="border-slate-700 text-slate-300">
            View Catalog
          </Button>
        </div>
      </div>

      {catalog && (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
          <h4 className="text-white font-medium mb-3">Attack Vector Catalog</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <div className="text-xs text-slate-500">Total Vectors</div>
              <div className="text-xl text-slate-200 font-mono">{catalog.total_vectors}</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <div className="text-xs text-slate-500">Categories</div>
              <div className="text-xl text-slate-200 font-mono">{catalog.categories?.length}</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <div className="text-xs text-slate-500">Critical</div>
              <div className="text-xl text-red-400 font-mono">{catalog.by_severity?.critical || 0}</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <div className="text-xs text-slate-500">High</div>
              <div className="text-xl text-orange-400 font-mono">{catalog.by_severity?.high || 0}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {catalog.categories?.map(cat => (
              <span key={cat} className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded">
                {cat.replace(/_/g, ' ')} ({catalog.by_category[cat]})
              </span>
            ))}
          </div>
        </div>
      )}

      {run && (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-medium">Run: {run.run_id}</h4>
            <Button onClick={getSummary} size="sm" className="bg-slate-700 hover:bg-slate-600 text-white">
              Get Summary
            </Button>
          </div>
          <p className="text-sm text-slate-400">{run.total_vectors} vectors queued</p>
        </div>
      )}

      {summary && (
        <div className={`rounded-xl p-6 border ${
          summary.grade === 'A' ? 'bg-green-500/5 border-green-500/30' :
          summary.grade === 'B' ? 'bg-blue-500/5 border-blue-500/30' :
          summary.grade === 'C' ? 'bg-yellow-500/5 border-yellow-500/30' :
          'bg-red-500/5 border-red-500/30'
        }`}>
          <div className="flex items-center gap-4 mb-4">
            <span className={`text-3xl font-bold px-3 py-1 rounded ${gradeBg(summary.grade)}`}>
              {summary.grade}
            </span>
            <div>
              <div className="text-sm text-slate-400">
                {summary.passed}/{summary.total_tests} passed ({(summary.pass_rate * 100).toFixed(1)}%)
              </div>
              <div className={`text-sm font-medium ${
                summary.deployment_recommendation === 'approved' ? 'text-green-400' :
                summary.deployment_recommendation === 'review_required' ? 'text-yellow-400' : 'text-red-400'
              }`}>{summary.deployment_recommendation?.replace(/_/g, ' ').toUpperCase()}</div>
            </div>
          </div>
          {summary.critical_failures > 0 && (
            <div className="text-sm text-red-400 bg-red-500/10 rounded p-2">
              {summary.critical_failures} critical failure(s): {summary.critical_failure_ids?.join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Room 5: Medical Records ──

function IncidentTab() {
  const [agentId, setAgentId] = useState('');
  const [severity, setSeverity] = useState('sev3');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [incident, setIncident] = useState(null);
  const [healthRecord, setHealthRecord] = useState(null);
  const [loading, setLoading] = useState(false);

  const createIncident = async () => {
    if (!agentId || !title) { toast.error('Agent ID and title required'); return; }
    setLoading(true);
    try {
      const data = await api.createIncident(agentId, severity, title, description || title);
      setIncident(data);
      toast.success(`Incident ${data.incident_id} created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create incident');
    }
    setLoading(false);
  };

  const loadHealthRecord = async () => {
    if (!agentId) { toast.error('Enter an agent ID'); return; }
    setLoading(true);
    try {
      const data = await api.getHealthRecord(agentId);
      setHealthRecord(data);
      toast.success(`Health grade: ${data.health_grade}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load health record');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-emerald-400" /> Incident Response
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Input placeholder="Agent ID (agt_...)" value={agentId} onChange={e => setAgentId(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" />
          <select value={severity} onChange={e => setSeverity(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-300 rounded px-3 py-2 text-sm">
            <option value="sev1">SEV1 - Critical: Active Breach</option>
            <option value="sev2">SEV2 - High: Imminent Threat</option>
            <option value="sev3">SEV3 - Moderate: Anomalous Behavior</option>
            <option value="sev4">SEV4 - Low: Performance Degradation</option>
          </select>
          <Input placeholder="Incident title" value={title} onChange={e => setTitle(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" />
          <Input placeholder="Description (optional)" value={description} onChange={e => setDescription(e.target.value)}
            className="bg-slate-800/50 border-slate-700 text-slate-200" />
        </div>
        <div className="flex gap-3">
          <Button onClick={createIncident} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
            Create Incident
          </Button>
          <Button onClick={loadHealthRecord} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <HeartPulse className="w-4 h-4 mr-2" /> Health Record
          </Button>
        </div>
      </div>

      {incident && (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded font-mono">{incident.incident_id}</span>
            <span className={`text-sm font-medium ${severityColor(incident.severity)}`}>
              {incident.severity_info?.name}
            </span>
          </div>
          <h4 className="text-white font-medium mb-3">Playbook ({incident.playbook?.length} steps)</h4>
          <div className="space-y-1">
            {incident.playbook?.map((step, i) => (
              <div key={i} className="flex items-center gap-3 text-sm py-1">
                <span className="text-xs text-slate-500 w-6 font-mono">{step.step}</span>
                <span className={`text-xs w-24 ${
                  step.phase === 'containment' ? 'text-red-400' :
                  step.phase === 'diagnosis' ? 'text-blue-400' :
                  step.phase === 'remediation' ? 'text-yellow-400' : 'text-green-400'
                }`}>{step.phase}</span>
                <span className="text-slate-300 flex-1">{step.description}</span>
                {step.automated && <span className="text-xs bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">auto</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {healthRecord && (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-center gap-4 mb-4">
            <span className={`text-3xl font-bold px-3 py-1 rounded ${gradeBg(healthRecord.health_grade)}`}>
              {healthRecord.health_grade}
            </span>
            <div>
              <div className="text-slate-200 font-medium">{healthRecord.agent_name}</div>
              <div className="text-sm text-slate-400">Score: {healthRecord.health_score}/100</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {Object.entries(healthRecord.vitals || {}).map(([key, val]) => (
              <div key={key} className="bg-slate-800/50 rounded-lg p-3">
                <div className="text-xs text-slate-500">{key.replace(/_/g, ' ')}</div>
                <div className={`text-sm font-mono ${
                  val === true ? 'text-green-400' : val === false ? 'text-red-400' :
                  val === 'unknown' || val === 'untested' ? 'text-slate-500' : 'text-slate-200'
                }`}>{String(val)}</div>
              </div>
            ))}
          </div>

          {healthRecord.recommendations?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-2">Recommendations</h4>
              {healthRecord.recommendations.map((rec, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-yellow-400/80 mb-1">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{rec}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ──

export default function AIDoctorPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
            <Link to="/agents" className="hover:text-slate-300">Agents</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">AI Doctor</span>
          </div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Stethoscope className="w-8 h-8 text-blue-400" />
            AI Doctor
          </h1>
          <p className="text-slate-400 mt-2">
            Comprehensive diagnostic toolkit for AI agent health, safety, and incident response
          </p>
        </div>

        <Tabs defaultValue="er" className="space-y-6">
          <TabsList className="bg-slate-800/50 border border-slate-700/50 p-1 rounded-xl">
            <TabsTrigger value="er" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400 gap-2">
              <HeartPulse className="w-4 h-4" /> ER
            </TabsTrigger>
            <TabsTrigger value="radiology" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 gap-2">
              <Eye className="w-4 h-4" /> Radiology
            </TabsTrigger>
            <TabsTrigger value="toxicology" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400 gap-2">
              <FlaskConical className="w-4 h-4" /> Toxicology
            </TabsTrigger>
            <TabsTrigger value="redteam" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400 gap-2">
              <Swords className="w-4 h-4" /> Red Team
            </TabsTrigger>
            <TabsTrigger value="incidents" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400 gap-2">
              <FileText className="w-4 h-4" /> Records
            </TabsTrigger>
          </TabsList>

          <TabsContent value="er"><ERTab /></TabsContent>
          <TabsContent value="radiology"><RadiologyTab /></TabsContent>
          <TabsContent value="toxicology"><ToxicologyTab /></TabsContent>
          <TabsContent value="redteam"><RedTeamTab /></TabsContent>
          <TabsContent value="incidents"><IncidentTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
